import { useState, useEffect, useRef, useMemo } from 'react';
import { api } from '@/lib/api';

export interface AdminEvent {
  id: string;
  name: string;
  status: string;
  dateStart: string;
  dateEnd: string;
}

/**
 * Shared hook for admin pages that need an event filter RadioGroup.
 *
 * Manages:
 * - eventFilter state ('all' | 'active' | specific event UUID)
 * - activeEventId (the currently active event)
 * - events list (all events for the RadioGroup)
 * - resolvedEventId — the UUID to pass to API queries (undefined when "all")
 *
 * Uses refs to avoid stale-closure issues in fetch callbacks.
 */
export function useAdminEventFilter() {
  const [eventFilter, setEventFilter] = useState<string>('active');
  const eventFilterRef = useRef<string>('active');

  const [activeEventId, setActiveEventId] = useState<string | null>(null);
  const activeEventIdRef = useRef<string | null>(null);

  const [events, setEvents] = useState<AdminEvent[]>([]);

  // Keep refs in sync so fetch callbacks always read the latest values
  useEffect(() => {
    eventFilterRef.current = eventFilter;
  }, [eventFilter]);

  useEffect(() => {
    activeEventIdRef.current = activeEventId;
  }, [activeEventId]);

  const fetchEvents = async () => {
    try {
      const res = await api.get<{ success: boolean; data: { id: string } }>('/events/active/public');
      if (res.success && res.data?.id) {
        activeEventIdRef.current = res.data.id;
        setActiveEventId(res.data.id);
      }
    } catch {
      // Non-critical — pages still work without active event
    }
    try {
      const evRes = await api.get<{ success: boolean; data: AdminEvent[] }>('/admin/events');
      if (evRes.success) {
        setEvents(evRes.data);
      }
    } catch {
      // Non-critical
    }
  };

  /**
   * Resolve the current filter to an eventId for API queries.
   * Returns undefined when "all" (no filtering).
   * Reads from refs so it's safe to call from any callback.
   */
  const getResolvedEventId = (): string | undefined => {
    const filter = eventFilterRef.current;
    const active = activeEventIdRef.current;
    if (filter === 'active' && active) return active;
    if (filter !== 'all' && filter !== 'active') return filter;
    return undefined;
  };

  /** Reactive resolved eventId for use in dependency arrays / render logic */
  const resolvedEventId = useMemo(() => {
    if (eventFilter === 'active' && activeEventId) return activeEventId;
    if (eventFilter !== 'all' && eventFilter !== 'active') return eventFilter;
    return undefined;
  }, [eventFilter, activeEventId]);

  return {
    eventFilter,
    setEventFilter,
    eventFilterRef,
    activeEventId,
    activeEventIdRef,
    events,
    fetchEvents,
    getResolvedEventId,
    resolvedEventId,
  };
}
